import classnames from "classnames";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useRegistrationContext } from "../Contexts/RegistrationContext";
import { ApplicantType, PageId } from "../Map/enums";
import { IObjectIDs, requestObjectIds, updateFeatures } from "../Map/rest";
import { Feature } from "ol";
import { config } from "../Map/config";
import { Form } from "./Form";
import { PrivacySection } from "../Registration/PrivacySection";

const formId = PageId.ApplicantForm;

export const ApplicantForm = (): JSX.Element => {
    const {
        formFeature,
        setFormFeature,
        resetForm,
        setResetForm,
        rawData,
        setRawData,
        setOverlay,
        validateForm,
        onFormUpdated,
        proceedClicked,
        setProceedClicked
    } = useRegistrationContext();

    const [applicantType, setApplicantType] = useState<ApplicantType>(
        formFeature ? formFeature.get("APPLICANTTYPE") : ApplicantType.Company
    );
    const [isFormValid, setIsFormValid] = useState<number>(null);
    const [formData, setFormData] = useState({});
    const [idRequested, setIdRequested] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        if (isFormValid && !formFeature) {
            console.log("Can not update feature without OBJECTID");
            setError(true);
            return;
        }
        isFormValid && proceedClicked && onFormValid();
    }, [isFormValid, proceedClicked]);

    const onFormValid = async () => {
        setOverlay({ isLoading: true });
        const updateResults = await updateFeatures([appendFormData()], "3");
        if (updateResults.status === 403 && updateResults.statusText === "Forbidden") {
            console.log("ReCaptcha authorization failed");
            setOverlay({ warning: config.reCaptchaErrorMessage });
            return;
        }
        if (!updateResults[0].success) {
            return;
        }
        setOverlay(null);
        onFormUpdated();
        console.log("gatavs iesniegšanai", updateResults[0]);
    };

    const createNewFeature = () => {
        const newFeature = new Feature();
        newFeature.setProperties({
            FORMINPUTSTATE: 1,
            APPLICATIONDATE: getFixedTimestamp(),
            APPLICANTTYPE: applicantType
        });
        // newFeature.setGeometry(registerFeature.getGeometry());
        return newFeature;
    };

    const appendFormData = useCallback(() => {
        const newprops = {};
        newprops["FORMINPUTSTATE"] = 100;
        const configFields =
            applicantType === ApplicantType.Individual
                ? config.forms.individualApplicantForm.fields
                : config.forms.companyApplicantForm.fields;
        configFields.map((f) => {
            newprops[f.field] = formData[f.field];
        });

        formFeature.setProperties(newprops);
        return formFeature;
    }, [formData, applicantType]);

    // STEP 1 - request ObjectId on first input change
    useEffect(() => {
        if (!Object.keys(rawData).length || formFeature || idRequested) {
            return;
        }
        setIdRequested(true);
        const newFeature = createNewFeature();
        requestObjectIds([newFeature], "3").then((ids: IObjectIDs[] | any) => {
            if (ids.status === 403 && ids.statusText === "Forbidden") {
                console.log("ReCaptcha authorization failed");
                setOverlay({ warning: config.reCaptchaErrorMessage });
                return;
            }
            if (!ids || !ids[0]) {
                console.log("Connection error while requesting OBJECTID");
                setError(true);
                return;
            }
            newFeature.setId(ids[0].objectId);
            newFeature.setProperties({
                OBJECTID: ids[0].objectId,
                GLOBALID: ids[0].globalId
            });
            setFormFeature(newFeature);
            setIdRequested(false);
            console.log("piešķirts OBJECTID", ids[0].objectId);
        });
    }, [rawData, /* applicantType,*/ idRequested]);

    const onFormValidated = (isValid: boolean) => {
        isValid ? setFormData(rawData) : setProceedClicked(false);
        setIsFormValid(isValid ? Date.now() : null);
    };

    const changeForm = (formType: ApplicantType) => {
        formType !== applicantType && setResetForm(Date.now());
        setApplicantType(formType);
    };

    const onFormChanged = (data) => {
        formFeature && setSameContactPerson(data, formFeature, rawData);
        setRawData(data);
    };

    const validate =
        ((validateForm.split("-")[0] as unknown) as PageId) === formId ? Number(validateForm.split("-")[1]) : 1;

    return (
        <>
            <PrivacySection></PrivacySection>
            <div className="tab-buttons">
                <TabButton type={ApplicantType.Individual} applicant={applicantType} onClick={changeForm}></TabButton>
                <TabButton type={ApplicantType.Company} applicant={applicantType} onClick={changeForm}></TabButton>
            </div>
            <Form
                fieldConfig={
                    applicantType === ApplicantType.Individual
                        ? config.forms.individualApplicantForm
                        : config.forms.companyApplicantForm
                }
                onChange={onFormChanged}
                validateForm={validate}
                onFormValidated={onFormValidated}
                resetForm={resetForm}
                formValues={Object.keys(rawData).length ? rawData : null}
            ></Form>
            {error ? <div className="network-error">Kļūda servera savienojumā!</div> : null}
        </>
    );
};

const TabButton = ({
    type,
    applicant,
    onClick
}: {
    type: ApplicantType;
    applicant: ApplicantType;
    onClick: CallableFunction;
}) => {
    return (
        <div
            className={classnames("tab-button unselectable", { "tab-button-active": type === applicant })}
            onClick={() => onClick(type)}
        >
            {type === ApplicantType.Individual ? "Fiziska persona" : "Juridiska persona"}
        </div>
    );
};

const getFixedTimestamp = () => {
    const date = new Date(Date.now());
    return Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
    );
};

const setSameContactPerson = (data, formFeature, rawData) => {
    if (data["SAMECONTACTPERSONMAIL"]) {
        formFeature.get("APPLICANTTYPE").toString() === "1" && (data["CONTACTPERSONMAIL"] = data["APPLICANTEMAIL"]);
    } else if (!data["SAMECONTACTPERSONMAIL"] && rawData["SAMECONTACTPERSONMAIL"]) {
        data["CONTACTPERSONMAIL"] = null;
    }
};
